import React, { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, CheckCircle, Calendar } from 'lucide-react'
import { OneOnOneSessionWithUsers } from '@/types'
import { SessionCard } from './SessionCard'

interface SessionsGridProps {
  sessions: OneOnOneSessionWithUsers[]
  isLoading?: boolean
  sessionsPerPage?: number
  onSessionAction?: (session: OneOnOneSessionWithUsers, action: 'view' | 'delete') => void
}

const DEFAULT_PAGE_SIZE = 6

export const SessionsGrid: React.FC<SessionsGridProps> = ({
  sessions,
  isLoading = false,
  sessionsPerPage = DEFAULT_PAGE_SIZE,
  onSessionAction,
}) => {
  const [currentPage, setCurrentPage] = useState(1)

  const { scheduledSessions, completedSessions } = useMemo(() => {
    const scheduled = sessions
      .filter((session) => session.status !== 'completed' && session.status !== 'cancelled')
      .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())

    const completed = sessions
      .filter((session) => session.status === 'completed')
      .sort((a, b) => new Date(b.completedAt ?? b.scheduledDate).getTime() - new Date(a.completedAt ?? a.scheduledDate).getTime())

    return { scheduledSessions: scheduled, completedSessions: completed }
  }, [sessions])

  const totalPages = Math.max(1, Math.ceil(completedSessions.length / sessionsPerPage))
  const safePage = Math.min(currentPage, totalPages)
  const startIndex = (safePage - 1) * sessionsPerPage
  const currentCompleted = completedSessions.slice(startIndex, startIndex + sessionsPerPage)

  const handlePreviousPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1))
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages))

  const handleView = (session: OneOnOneSessionWithUsers) => {
    onSessionAction?.(session, 'view')
  }

  const handleDelete = (session: OneOnOneSessionWithUsers) => {
    onSessionAction?.(session, 'delete')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-border border-t-foreground"></div>
          <span className="text-muted-foreground">Loading 1-on-1 sessions...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-10">
      {scheduledSessions.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Upcoming Sessions</h2>
              <p className="text-sm text-gray-600">{scheduledSessions.length} awaiting completion</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scheduledSessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onView={() => handleView(session)}
                onDelete={() => handleDelete(session)}
              />
            ))}
          </div>
        </section>
      )}

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Completed Sessions</h2>
              <p className="text-sm text-gray-600">{completedSessions.length} total</p>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Page {safePage} of {totalPages}
          </div>
        </div>

        {currentCompleted.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentCompleted.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onView={() => handleView(session)}
                onDelete={() => handleDelete(session)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Completed Sessions</h3>
            <p className="text-gray-600">No completed sessions match the current filter criteria.</p>
          </div>
        )}

        {completedSessions.length > sessionsPerPage && (
          <div className="flex items-center justify-center gap-4 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={safePage === 1}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            <span className="text-sm text-gray-600">Page {safePage} of {totalPages}</span>

            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={safePage === totalPages}
              className="flex items-center gap-2"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </section>
    </div>
  )
}
